/**
 * Source Tracker - Captura origem do tráfego e fonte de inscrições
 *
 * Detecta automaticamente:
 * - UTM parameters (utm_source, utm_medium, utm_campaign, etc)
 * - Referrer (Instagram, LinkedIn, Google, Facebook, etc)
 * - Device info (mobile, desktop, tablet)
 * - Browser e OS
 * - Geolocalização por IP (país, região, cidade)
 * - Idioma, timezone, resolução de tela
 * - Rastreamento de sessão e tempo na página
 */

import apiClient from '../services/api';

const STORAGE_KEY = 'rsvp_source_data';
const SESSION_KEY = 'rsvp_session_id';
const PAGE_START_KEY = 'rsvp_page_start';
const STORAGE_EXPIRY_DAYS = 30;

/**
 * Detecta a fonte de tráfego baseado no referrer
 */
function detectSourceFromReferrer(referrer) {
  if (!referrer) return { source: 'direct', medium: 'none' };

  const url = referrer.toLowerCase();

  // Social Media
  if (url.includes('instagram.com')) return { source: 'instagram', medium: 'social' };
  if (url.includes('facebook.com') || url.includes('fb.com') || url.includes('fb.me')) return { source: 'facebook', medium: 'social' };
  if (url.includes('linkedin.com') || url.includes('lnkd.in')) return { source: 'linkedin', medium: 'social' };
  if (url.includes('twitter.com') || url.includes('x.com') || url.includes('t.co')) return { source: 'twitter', medium: 'social' };
  if (url.includes('tiktok.com')) return { source: 'tiktok', medium: 'social' };
  if (url.includes('whatsapp.com') || url.includes('wa.me')) return { source: 'whatsapp', medium: 'social' };
  if (url.includes('t.me') || url.includes('telegram')) return { source: 'telegram', medium: 'social' };
  if (url.includes('pinterest.com') || url.includes('pin.it')) return { source: 'pinterest', medium: 'social' };
  if (url.includes('reddit.com')) return { source: 'reddit', medium: 'social' };

  // Search Engines
  if (url.includes('google.com') || url.includes('google.')) return { source: 'google', medium: 'organic' };
  if (url.includes('bing.com')) return { source: 'bing', medium: 'organic' };
  if (url.includes('yahoo.com')) return { source: 'yahoo', medium: 'organic' };
  if (url.includes('duckduckgo.com')) return { source: 'duckduckgo', medium: 'organic' };
  if (url.includes('baidu.com')) return { source: 'baidu', medium: 'organic' };

  // Video Platforms
  if (url.includes('youtube.com') || url.includes('youtu.be')) return { source: 'youtube', medium: 'video' };
  if (url.includes('vimeo.com')) return { source: 'vimeo', medium: 'video' };

  // Email Providers (se vier de webmail)
  if (url.includes('mail.google.com') || url.includes('outlook.com') || url.includes('mail.yahoo.com')) {
    return { source: 'email', medium: 'email' };
  }

  // Generic referral
  try {
    const domain = new URL(referrer).hostname.replace('www.', '');
    return { source: domain, medium: 'referral' };
  } catch {
    return { source: 'unknown', medium: 'referral' };
  }
}

/**
 * Extrai UTM parameters da URL
 */
function extractUtmParams(url = window.location.href) {
  try {
    const params = new URLSearchParams(new URL(url).search);

    return {
      utm_source: params.get('utm_source') || params.get('source'),
      utm_medium: params.get('utm_medium') || params.get('medium'),
      utm_campaign: params.get('utm_campaign') || params.get('campaign'),
      utm_content: params.get('utm_content') || params.get('content'),
      utm_term: params.get('utm_term') || params.get('term')
    };
  } catch (error) {
    console.warn('Erro ao extrair UTM params:', error);
    return {};
  }
}

/**
 * Detecta tipo de dispositivo
 */
function detectDevice() {
  const ua = navigator.userAgent.toLowerCase();

  // Tablet check first (mais específico)
  if (/(ipad|tablet|playbook|silk)|(android(?!.*mobile))/i.test(ua)) {
    return 'tablet';
  }

  // Mobile check
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile|wpdesktop/i.test(ua)) {
    return 'mobile';
  }

  return 'desktop';
}

/**
 * Detecta navegador
 */
function detectBrowser() {
  const ua = navigator.userAgent;

  // Ordem importa - verificar mais específicos primeiro
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('MSIE') || ua.includes('Trident')) return 'Internet Explorer';

  return 'Unknown';
}

/**
 * Detecta sistema operacional
 */
function detectOS() {
  const ua = navigator.userAgent;

  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('X11') || ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';

  return 'Unknown';
}

/**
 * Detecta idioma do navegador
 */
function detectLanguage() {
  try {
    return navigator.language || navigator.userLanguage || 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Detecta timezone do usuário
 */
function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Detecta resolução da tela
 */
function detectScreenResolution() {
  try {
    return `${window.screen.width}x${window.screen.height}`;
  } catch {
    return 'unknown';
  }
}

/**
 * Gera ou recupera Session ID (UUID)
 * Armazenado em sessionStorage (expira quando fecha o navegador)
 */
function getOrCreateSessionId() {
  try {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      // Gera UUID v4 simples
      sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return null;
  }
}

/**
 * Inicia contagem de tempo na página
 */
function startPageTimer() {
  try {
    sessionStorage.setItem(PAGE_START_KEY, Date.now().toString());
  } catch {
    // Silent fail
  }
}

/**
 * Calcula tempo gasto na página (em segundos)
 */
function getTimeOnPage() {
  try {
    const startTime = sessionStorage.getItem(PAGE_START_KEY);
    if (!startTime) return 0;
    return Math.floor((Date.now() - parseInt(startTime)) / 1000);
  } catch {
    return 0;
  }
}

/**
 * Busca geolocalização por IP usando ip-api.com (free, 45 req/min)
 * Retorna: country, region, city, latitude, longitude, isp
 */
async function fetchGeolocation() {
  try {
    // ip-api.com - 45 requests/minute, sem CORS issues
    const response = await fetch('http://ip-api.com/json/', {
      method: 'GET'
    });

    if (!response.ok) {
      console.warn('[SourceTracker] Erro ao buscar geolocalização:', response.status);
      return null;
    }

    const data = await response.json();

    // ip-api.com retorna status: "fail" se falhar
    if (data.status === 'fail') {
      console.warn('[SourceTracker] ip-api.com retornou erro:', data.message);
      return null;
    }

    console.log('[SourceTracker] 📍 Geolocalização:', data.city, data.regionName, data.country);

    return {
      country: data.country || null,
      region: data.regionName || null,
      city_ip: data.city || null,
      latitude: data.lat || null,
      longitude: data.lon || null,
      isp: data.isp || data.org || null
    };
  } catch (error) {
    console.warn('[SourceTracker] Erro ao buscar geolocalização:', error);
    return null;
  }
}

/**
 * Detecta plataforma (landing_page ou widget)
 */
function detectPlatform() {
  // Se está rodando dentro de um iframe, provavelmente é widget
  if (window.self !== window.top) {
    return 'widget';
  }

  // Se o domínio é diferente do parent, é widget embedado
  try {
    if (window.parent && window.parent.location.hostname !== window.location.hostname) {
      return 'widget';
    }
  } catch (e) {
    // Cross-origin error = widget embedado
    return 'widget';
  }

  // Default: landing page
  return 'landing_page';
}

/**
 * Captura e armazena dados de origem
 * Chamado automaticamente quando o usuário acessa a página
 */
export async function captureSourceData() {
  try {
    // Inicia timer da página
    startPageTimer();

    // Verifica se já existe no storage (primeira visita wins)
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) {
      const data = JSON.parse(existing);
      // Se ainda válido (dentro do período), não sobrescreve
      if (data.expires_at && new Date(data.expires_at) > new Date()) {
        console.log('[SourceTracker] Usando dados existentes:', data.source, data.medium);
        return data;
      }
    }

    // Captura novos dados
    const utmParams = extractUtmParams();
    const autoDetected = detectSourceFromReferrer(document.referrer);

    // Busca geolocalização por IP (async)
    const geoData = await fetchGeolocation();

    // Gera/recupera session_id
    const sessionId = getOrCreateSessionId();

    if (!sessionId) {
      console.error('[SourceTracker] ERRO CRÍTICO: Não foi possível gerar session_id! sessionStorage pode estar bloqueado.');
    } else {
      console.log('[SourceTracker] Session ID gerado/recuperado:', sessionId);
    }

    const sourceData = {
      // UTM params têm prioridade sobre detecção automática
      source: utmParams.utm_source || autoDetected.source,
      medium: utmParams.utm_medium || autoDetected.medium,
      campaign: utmParams.utm_campaign || null,
      content: utmParams.utm_content || null,
      term: utmParams.utm_term || null,

      // Metadata
      referrer: document.referrer || null,
      landing_page: window.location.href,
      platform: detectPlatform(),
      device_type: detectDevice(),
      browser: detectBrowser(),
      os: detectOS(),
      user_agent: navigator.userAgent,

      // Dados demográficos / técnicos
      language: detectLanguage(),
      timezone: detectTimezone(),
      screen_resolution: detectScreenResolution(),

      // Sessão
      session_id: sessionId,
      pages_visited: 1,
      is_bounce: false,

      // Geolocalização por IP
      ...(geoData || {}),

      // Timestamps
      captured_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString()
    };

    // Armazena no localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sourceData));

    console.log('[SourceTracker] Capturado:', sourceData.source, sourceData.medium, sourceData.campaign || '(sem campanha)');
    if (geoData) {
      console.log('[SourceTracker] Localização:', geoData.city_ip, geoData.region, geoData.country);
    }

    return sourceData;
  } catch (error) {
    console.error('[SourceTracker] Erro ao capturar dados:', error);
    return null;
  }
}

/**
 * Retorna dados de origem armazenados
 */
export function getSourceData() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;

    const parsed = JSON.parse(data);

    // Verifica se expirou
    if (parsed.expires_at && new Date(parsed.expires_at) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('[SourceTracker] Erro ao ler dados:', error);
    return null;
  }
}

/**
 * Limpa dados de origem
 * Útil para testes ou após conversão bem-sucedida (opcional)
 */
export function clearSourceData() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[SourceTracker] Dados limpos');
  } catch (error) {
    console.error('[SourceTracker] Erro ao limpar dados:', error);
  }
}

/**
 * Prepara dados para envio à API
 * Remove campos desnecessários e formata corretamente
 */
export function getSourceDataForApi() {
  const data = getSourceData();
  if (!data) return null;

  // Calcula tempo na página no momento do submit
  const timeOnPage = getTimeOnPage();

  // Remove campos internos de controle
  const { expires_at, ...apiData } = data;

  return {
    ...apiData,
    time_on_page: timeOnPage
  };
}

/**
 * ============================================
 * TRACKING PASSIVO - Registra todas as visitas
 * ============================================
 */

/**
 * Obtém a base URL da API a partir do client inicializado
 */
function getApiBaseUrl() {
  if (apiClient && apiClient.defaults && apiClient.defaults.baseURL) {
    return apiClient.defaults.baseURL;
  }
  console.warn('[SourceTracker] API client não inicializado, tracking desabilitado');
  return null;
}

/**
 * Envia pageview para o backend IMEDIATAMENTE
 * Registra a sessão mesmo sem conversão
 */
export async function trackPageView() {
  try {
    const sourceData = getSourceData();
    if (!sourceData) {
      console.warn('[SourceTracker] Sem dados de origem para enviar');
      return;
    }

    // Validação crítica: session_id é obrigatório
    if (!sourceData.session_id) {
      console.error('[SourceTracker] session_id está vazio! Dados:', sourceData);
      // Tenta gerar um novo
      sourceData.session_id = getOrCreateSessionId();
      if (!sourceData.session_id) {
        console.error('[SourceTracker] Não foi possível gerar session_id. sessionStorage bloqueado?');
        return;
      }
    }

    // SEMPRE usa o session_id atual do sessionStorage (não o cacheado no localStorage)
    sourceData.session_id = getOrCreateSessionId();

    console.log('[SourceTracker] Enviando pageview com session_id:', sourceData.session_id);

    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return;

    // Envia para API
    const response = await fetch(`${apiBaseUrl}/track/pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(sourceData)
    });

    const result = await response.json();

    if (result.success) {
      console.log('[SourceTracker] Pageview registrado:', result.action);
    } else {
      console.warn('[SourceTracker] Falha ao registrar pageview:', result);
    }
  } catch (error) {
    console.warn('[SourceTracker] Erro ao enviar pageview:', error);
  }
}

/**
 * Atualiza tempo na página ao sair (usando beacon)
 * Garante envio mesmo ao fechar aba/navegador
 */
export function trackPageUnload() {
  try {
    const sourceData = getSourceData();
    if (!sourceData || !sourceData.session_id) return;

    const timeOnPage = getTimeOnPage();

    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return;

    // FormData é mais compatível com sendBeacon e Laravel
    const formData = new FormData();
    formData.append('session_id', sourceData.session_id);
    formData.append('time_on_page', timeOnPage);

    // navigator.sendBeacon garante envio mesmo ao fechar página
    const success = navigator.sendBeacon(`${apiBaseUrl}/track/pageview`, formData);

    console.log('[SourceTracker] Beacon enviado:', success ? 'sucesso' : 'falhou', '| Tempo:', timeOnPage, 's');
  } catch (error) {
    console.warn('[SourceTracker] Erro ao enviar beacon:', error);
  }
}

/**
 * Registra conversão (quando usuário submete formulário)
 */
export async function trackConversion(participantId) {
  try {
    const sourceData = getSourceData();
    if (!sourceData) {
      console.warn('[SourceTracker] Sem dados de origem para conversão');
      return;
    }

    if (!sourceData.session_id) {
      console.error('[SourceTracker] session_id vazio na conversão!');
      return;
    }

    if (!participantId) {
      console.error('[SourceTracker] participant_id vazio na conversão!');
      return;
    }

    console.log('[SourceTracker] Registrando conversão...', {
      session_id: sourceData.session_id,
      participant_id: participantId
    });

    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return;

    const response = await fetch(`${apiBaseUrl}/track/conversion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        session_id: sourceData.session_id,
        participant_id: participantId
      })
    });

    if (!response.ok) {
      console.error('[SourceTracker] Erro HTTP na conversão:', response.status);
      const errorText = await response.text();
      console.error('[SourceTracker] Resposta:', errorText);
      return;
    }

    const result = await response.json();

    if (result.success) {
      console.log('[SourceTracker] ✅ Conversão registrada para participant:', participantId);
    } else {
      console.warn('[SourceTracker] ❌ Falha ao registrar conversão:', result);
    }
  } catch (error) {
    console.error('[SourceTracker] Erro ao registrar conversão:', error);
  }
}

/**
 * Inicializa tracking passivo completo
 * Deve ser chamado no onMounted() do componente
 */
export async function initializeTracking() {
  // 1. Captura dados de origem
  await captureSourceData();

  // 2. Envia pageview IMEDIATAMENTE
  await trackPageView();

  // 3. Configura beacon no unload
  window.addEventListener('beforeunload', trackPageUnload);
  window.addEventListener('pagehide', trackPageUnload); // Para iOS/Safari
}

/**
 * Limpa listeners de tracking
 */
export function cleanupTracking() {
  window.removeEventListener('beforeunload', trackPageUnload);
  window.removeEventListener('pagehide', trackPageUnload);
}
